<template>
  <page-wrapper ref="pageEl" :has-pad="true" title="ประวัติการใช้งาน">
    <div class="header">
      <h3 class="code">{{ header.code }}</h3>
      <div class="usage">การใช้งาน {{ header.usage }}</div>
      <div class="period">ระยะเวลา {{ header.period }}</div>
    </div>
    <p v-if="total" class="mb-2 mt-2">{{ totalCount }}</p>
    <div class="usage-item header">
      <div class="customer-name">ชื่อผู้ใช้</div>
      <div class="created-at">ใช้งานเมื่อ</div>
      <div class="package-name">แพ็กเกจ</div>
      <div></div>
    </div>
    <div v-for="usage in usagePresenters" :key="usage.id" class="usage-item">
      <div class="customer-name">
        <label class="label">ชื่อผู้ใช้</label>
        {{ usage.customerName }}
      </div>
      <div class="created-at">
        <label class="label">ใช้งานเมื่อ</label>
        {{ usage.createdAt }}
      </div>
      <div class="package-name">
        <label class="label">แพ็กเกจ</label>
        {{ usage.package.name }}
      </div>
      <sc-button :route="usage.route">ดูรายละเอียด</sc-button>
    </div>
    <div class="text-end mt-3">
      <el-pagination
        :total="total"
        :size="10"
        :current-page="query.page"
        background
        layout="prev, pager, next"
        @current-change="handleCurrentChange"
      ></el-pagination>
    </div>
  </page-wrapper>
</template>

<script>
import { DateTime } from "luxon";
import { Pagination as ElPagination } from "element-ui";
import PageWrapper from "@/lib/components/PageWrapper";
import ScButton from "@/lib/components/ScButton";
import { baanRueJaiClient } from "@/dependencies";
import { toLocaleTH } from "@/lib/dateTime";

export default {
  components: {
    PageWrapper,
    ScButton,
    ElPagination
  },
  data() {
    const coupon = null;
    const usages = null;
    const total = 1;
    const query = {};

    return { usages, coupon, total, query };
  },
  computed: {
    header() {
      if (!this.usages || !this.coupon)
        return {
          code: "",
          usage: "",
          period: ""
        };

      const usage = this.coupon.usage_quota
        ? `${this.total}/${this.coupon.usage_quota}`
        : "ไม่จำกัด";

      const { effective_date: effectiveDateISO, expiry_date: expiryDateISO } = this.coupon;
      const effectiveDate = toLocaleTH(DateTime.fromISO(effectiveDateISO));
      const expiryDate = toLocaleTH(DateTime.fromISO(expiryDateISO));

      const isSameYear = effectiveDate.hasSame(expiryDate, "year");
      const isSameMonth = effectiveDate.hasSame(expiryDate, "month");

      const format = "d MMM yyyy";

      let period;
      if (!isSameYear) {
        period = `${effectiveDate.toFormat(format)} - ${expiryDate.toFormat(format)}`;
      } else if (!isSameMonth) {
        period = `${effectiveDate.toFormat("dd MMM")} - ${expiryDate.toFormat(format)}`;
      } else {
        period = `${effectiveDate.toFormat("dd")} - ${expiryDate.toFormat(format)}`;
      }

      return {
        code: this.coupon.code,
        usage,
        period
      };
    },
    usagePresenters() {
      if (!this.usages) return [];

      return this.usages.map(usage => {
        const { id, user_profile: userProfile, created_at: createdAtISO, subscription_history: subscriptionHistory } = usage;
        const customerName = `${userProfile.first_name} ${
          userProfile.last_name
        }`;
        const createdAt = toLocaleTH(DateTime.fromISO(createdAtISO)).toFormat(
          "dd MMM yyyy HH:mm น."
        );
        const route = {
          name: "SubscriberDetailPage",
          params: { id: subscriptionHistory.subscription_id }
        };

        const selectedPackage = { name: usage.invoice.package.name };

        return { id, customerName, createdAt, route, package: selectedPackage };
      });
    },
    code() {
      if (!this.coupon) return "";

      return `รหัสส่วนลด ${this.coupon.code}`;
    },
    totalCount() {
      if (!this.usages) {
        return "";
      }

      const limit = 10;
      const total = this.total.toLocaleString();
      const currentTotalItem = this.usages.length;
      const isLastPage = limit > currentTotalItem;
      const currentPage = this.query.page || 1;
      const end = (isLastPage ? total : currentPage * limit).toLocaleString();
      const start = (
        (isLastPage ? limit * currentPage - limit : end - limit) + 1
      ).toLocaleString();

      return `แสดงผล ${start} - ${end} จากทั้งหมด ${total}`;
    }
  },
  watch: {
    "$route.query": async function watchRouteQuery() {
      this.setupQuery();
      await this.setupUsages();

      this.$refs.pageEl.$el.querySelector(".page-content").scrollTop = 0;
    }
  },
  created() {
    this.setup();
  },
  methods: {
    setup() {
      this.setupData();
    },
    setupQuery() {
      this.query = {
        page: this.$route.query.page || 1
      };
    },
    async setupData() {
      Promise.all([await this.setupCoupon(), await this.setupUsages()]);
    },
    async setupUsages() {
      const { histories, count } = await this.fetchUsages();

      this.usages = histories;
      this.total = count;
    },
    async fetchUsages() {
      const { id } = this.$route.params;
      const { page = 1 } = this.$route.query;
      const response = await baanRueJaiClient.admin.subscriptions.coupons.getUsages(
        id,
        page
      );

      return response.data.result;
    },
    async setupCoupon() {
      const coupon = await this.fetchCoupon();

      this.coupon = coupon;
    },
    async fetchCoupon() {
      const { id } = this.$route.params;
      const response = await baanRueJaiClient.admin.subscriptions.coupons.get(
        id
      );

      return response.data.result;
    },
    handleCurrentChange(page) {
      const route = {
        name: this.$route.name,
        query: { ...this.$route.query, page }
      };

      this.$router.replace(route);
    }
  }
};
</script>

<style lang="scss" scoped>
.usage-item {
  display: grid;
  grid-gap: 5px;

  border-bottom: 1px solid #ddd;
  padding: 10px 0;

  &.header {
    display: none;
    font-size: 14px;
    font-weight: bold;
  }

  @media (min-width: 900px) {
    grid-template-columns: 300px 250px 1fr 150px;
    grid-gap: 15px;
    align-items: center;

    &.header {
      display: grid;
    }
  }

  > * {
    display: grid;
    grid-gap: 15px;
    grid-template-columns: 90px 1fr;

    @media (min-width: 900px) {
      grid-template-columns: 1fr;
    }
  }
}

.label {
  margin: 0;

  @media (min-width: 900px) {
    display: none;
  }
}
</style>
